import {
  Modal,
  ModalContent,
  ModalHeader,
} from '@lightspeed/design-system-react'
import { PAYMENT_TYPES } from '@vend/business-components'
import { ApiRetailerPaymentType } from '@vend/types'
import { reactular } from '@vendhq/reactular'
import classNames from 'classnames'
import React, { FC } from 'react'
import { ClientApiSaleData } from '../../../../factories/sale-transformer'
import { usePaymentManager } from '../../../../ng-compat/hooks'
import { useTrackPaymentDuration } from '../../hooks/useTrackPaymentDuration'
import {
  AcceptStep,
  PAYMENT_GATEWAY_IFRAME,
  PAYMENT_STEPS,
  RejectStep,
} from '../integrated-payment-steps.constant'
import { getBasePaymentPortalUrl } from './integratedPaymentModalHelpers'
import { useGatewayHandler } from './useGatewayHandler'

export interface PaymentData {
  currency: string
  retailerPaymentTypeId: string
  amount: string
  paymentType: ApiRetailerPaymentType
  registerId: string
  externalTransactionIds: string[]
  registerSalePaymentIds: string[]
  paymentId: string
}

interface Props {
  saleData: ClientApiSaleData
  paymentData: PaymentData
  close: (val?: Promise<AcceptStep>) => void
}

export const IntegratedPaymentModal: FC<Props> = ({
  saleData,
  paymentData,
  close,
}) => {
  const paymentManager = usePaymentManager()

  const gatewaySrc = paymentManager.getPaymentPortalUrl({
    amount: paymentData.amount,
    basePaymentPortalUrl: getBasePaymentPortalUrl(paymentData.paymentType),
    retailerPaymentTypeId: paymentData.paymentType.id,
    currency: paymentData.currency,
    customerId: saleData.customer_id,
    referenceId: paymentData.paymentId,
    registerId: paymentData.registerId,
  })

  const trackEnd = useTrackPaymentDuration(
    'Payments API',
    paymentData.registerId,
    paymentData.amount,
    paymentData.paymentType.type_id,
    {
      isReact: true,
      sale: saleData.id,
      url:
        paymentData.paymentType.type_id === PAYMENT_TYPES.OTHER_PAYMENT_METHOD
          ? paymentData.paymentType.config?.url
          : undefined,
    }
  )

  const accept = (data: AcceptStep) => {
    trackEnd({ step: data.step })
    close(Promise.resolve(data))
  }

  const reject = (data: RejectStep) => {
    trackEnd({ step: data.step })
    close(Promise.reject(data))
  }

  const { enableClose, isFullscreen, header } = useGatewayHandler({
    gatewaySrc,
    paymentData,
    saleData,
    iframeId: PAYMENT_GATEWAY_IFRAME,
    onAccept: accept,
    onReject: reject,
  })

  return (
    <Modal
      size="medium"
      onClose={() => reject({ step: PAYMENT_STEPS.DISMISSED })}
      dismissable={enableClose}
      className={classNames('integrated-payment-modal', {
        'integrated-payment-modal--fullscreen': isFullscreen,
      })}
    >
      {header && <ModalHeader className="vd-mb0">{header}</ModalHeader>}
      <ModalContent scrollable={false} className="vd-ma0">
        <iframe
          id={PAYMENT_GATEWAY_IFRAME}
          // without this, there is apparently no way to focus the inner elements
          // of the iframe via keyboard only.
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          src={gatewaySrc}
          frameBorder={0}
          className={classNames('integrated-payment-iframe', {
            'integrated-payment-iframe--fullscreen': isFullscreen,
          })}
          title={`${paymentData.paymentType.name} Payment Modal`}
        />
      </ModalContent>
    </Modal>
  )
}

export const IntegratePaymentModalComponent = reactular(
  IntegratedPaymentModal,
  ['close', 'paymentData', 'saleData'],
  'reactWrapper'
)
